import { useCallback, useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { KeenIcon } from '@/components';
import { useAuthContext } from '@/auth';
import ailaService from '@/services/aila/ailaService';

/**
 * "Aila" AI summary card for a contact or matter detail page. One component for
 * both — only the parent type differs. The backend caches summaries for a
 * short while; Refresh asks Aila to re-read the file.
 */
const generatedAgo = (generatedAt) => {
  if (!generatedAt) return '';

  try {
    return formatDistanceToNow(new Date(generatedAt), { addSuffix: true });
  } catch {
    return '';
  }
};

const AilaSummaryPanel = ({ parentType, parentId }) => {
  const { auth } = useAuthContext();
  // The login payload carries the firm's AI Features setting, so when it's
  // off the card never renders (no flicker). Older stored sessions lack the
  // key — those still fetch, and the backend's disabled flag hides the card.
  const aiEnabled = auth?.user?.ai_features_enabled !== false;
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const load = useCallback(
    async (refresh = false) => {
      setLoading(true);
      setFailed(false);
      try {
        const { status, data } = await ailaService.getSummary({ parentType, parentId, refresh });
        if (status && data?.disabled) {
          // The firm turned AI features off in settings — hide the card.
          setDisabled(true);
        } else if (status && data?.summary) {
          setResult(data);
        } else {
          setFailed(true);
        }
      } catch (error) {
        console.error('Error', error);
        setFailed(true);
      } finally {
        setLoading(false);
      }
    },
    [parentType, parentId]
  );

  useEffect(() => {
    if (parentId && aiEnabled) load();
  }, [parentId, aiEnabled, load]);

  if (!parentId || !aiEnabled || disabled) return null;

  return (
    <div className="mb-5 card">
      <div className="card-header">
        <h3 className="flex items-center gap-2 card-title">
          <KeenIcon icon="technology-2" className="text-primary" />
          AILA
          <span className="text-xs font-normal text-gray-600">AI case summary</span>
        </h3>
        <div className="flex items-center gap-3">
          {!loading && result?.generated_at ? (
            <span className="text-xs text-gray-600">Updated {generatedAgo(result.generated_at)}</span>
          ) : null}
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={() => load(true)}
            disabled={loading}
          >
            <KeenIcon icon="arrows-circle" />
            Refresh
          </button>
        </div>
      </div>
      <div className="p-5 card-body">
        {loading ? (
          <p className="text-sm text-gray-500">AILA is reading the file…</p>
        ) : failed ? (
          <p className="text-sm text-gray-500">
            AILA could not generate a summary right now.{' '}
            <button type="button" className="text-primary hover:underline" onClick={() => load()}>
              Try again
            </button>
          </p>
        ) : (
          <>
            <p className="text-sm leading-relaxed text-gray-800">{result.summary}</p>
            <p className="mt-2 text-xs text-gray-500">
              AI-generated — double-check dates against the file before acting on them.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export { AilaSummaryPanel };
